import { useContext, useState, useEffect, useCallback } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";

import HeadlessDialog from "@components/Dialog";
import ScrollableModalBody from "@components/ScrollableModalBody";
import { IClientDTO } from "@interfaces/IClientDTO";
import { SnackbarContext } from "@components/constant_components/providers/SnackbarProvider";
import Button from "@components/Button";
import FormikInput from "@components/form/FormikInput";
import FormikCombobox from "@components/form/FormikCombobox";
import { getStatesWithCombinedValue } from "@utils/states";
import { ISelect } from "@interfaces/ISelect";
import ClientService from "@service/ClientService";
import Spinner from "@components/Spinner";

interface IClientEditProps {
  id: number;
  selectedClient: IClientDTO | null;
  isOpen: boolean;
  onClose: () => void;
}

interface IClientForm {
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
}

const clientSchema = Yup.object().shape({
  name: Yup.string()
    .required("Name is required")
    .min(2, "Name must be at least 2 characters")
    .max(255, "Name must not exceed 255 characters"),
  address: Yup.string().max(255).nullable(),
  city: Yup.string().max(100).nullable(),
  state: Yup.string().nullable(),
  zip: Yup.string().max(10).nullable(),
  phone: Yup.string().max(20).nullable(),
});

export function ClientCreateEdit({
  id,
  selectedClient,
  isOpen,
  onClose,
}: IClientEditProps): JSX.Element {
  const { triggerSnackbar } = useContext(SnackbarContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [states, setStates] = useState<ISelect[]>([]);

  useEffect(() => {
    const statesWithCombinedValue = getStatesWithCombinedValue();
    setStates(
      statesWithCombinedValue.map((state, idx) => ({
        id: idx,
        value: state.value,
        code: state.code,
      })) as ISelect[]
    );
  }, []);

  const getStateValueByCode = useCallback(
    (stateCode?: string): string => {
      const state = states.find((s) => s.code === stateCode);
      return state ? state.value : "";
    },
    [states]
  );

  const getStateCodeByValue = useCallback(
    (stateValue: string): string => {
      const state = states.find((s) => s.value === stateValue);
      return state?.code ? state.code : "";
    },
    [states]
  );

  const isEditMode = id > 0 && selectedClient !== null;

  const initialValues: IClientForm = isEditMode
    ? {
        name: selectedClient!.name ?? "",
        address: selectedClient!.address ?? "",
        city: selectedClient!.city ?? "",
        state: getStateValueByCode(selectedClient!.state),
        zip: selectedClient!.zip ?? "",
        phone: selectedClient!.phone ?? "",
      }
    : {
        name: "",
        address: "",
        city: "",
        state: "",
        zip: "",
        phone: "",
      };

  const handleSubmit = async (values: IClientForm) => {
    setIsSubmitting(true);
    try {
      const payload = { ...values, state: getStateCodeByValue(values.state) };
      if (isEditMode) {
        await ClientService.updateClient({ id, ...payload });
        triggerSnackbar("Client updated successfully", "success");
      } else {
        await ClientService.createClient(payload);
        triggerSnackbar("Client created successfully", "success");
      }
      onClose();
    } catch (error: any) {
      console.error("Error saving client:", error);
      const errorMessage =
        error?.response?.data?.error ||
        error?.message ||
        "An error occurred while saving the client";
      triggerSnackbar(errorMessage, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <HeadlessDialog
      isOpen={isOpen}
      setIsOpen={onClose}
      onClose={onClose}
      title={isEditMode ? "Edit Client" : "Create Client"}
      description=""
      buttonText="Close"
    >
      <Formik
        initialValues={initialValues}
        validationSchema={clientSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {() => (
          <Form>
            <ScrollableModalBody>
              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                <div className="md:tw-col-span-2">
                  <Field
                    as={FormikInput}
                    label="Name"
                    name="name"
                    type="text"
                    placeholder="Client name"
                    required
                  />
                </div>
                <div className="md:tw-col-span-2">
                  <Field
                    as={FormikInput}
                    label="Address"
                    name="address"
                    type="text"
                    placeholder="Street address"
                  />
                </div>
                <Field
                  as={FormikInput}
                  label="City"
                  name="city"
                  type="text"
                  placeholder="City"
                />
                <Field
                  name="state"
                  label="State"
                  options={states}
                  as={FormikCombobox}
                />
                <Field
                  as={FormikInput}
                  label="Zip"
                  name="zip"
                  type="text"
                  placeholder="Zip code"
                  maxLength={10}
                />
                <Field
                  as={FormikInput}
                  label="Phone"
                  name="phone"
                  type="text"
                  placeholder="Phone number"
                  maxLength={20}
                />
              </div>
            </ScrollableModalBody>

            <div className="tw-flex tw-justify-end tw-gap-2 tw-p-4 tw-border-t">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="tw-px-4 tw-py-2 tw-bg-blue-600 tw-text-white tw-rounded hover:tw-bg-blue-700"
              >
                {isSubmitting ? (
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <Spinner className="tw-w-4 tw-h-4" />
                    <span>Saving...</span>
                  </div>
                ) : isEditMode ? (
                  "Update"
                ) : (
                  "Create"
                )}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </HeadlessDialog>
  );
}
